import React, { useEffect } from 'react';
import { lazy } from 'react-loosely-lazy';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { ExperienceTracker as ViewExperienceTracker } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracker-consumer/tracker-base/index.tsx';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracking-provider/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import ErrorImageUrl from '@atlassian/jira-illustrations/src/ui/adg4/jira/heroes/components/icecream-accident';
import EmptyImageUrl from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/cumulative-flow-diagram';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import Placeholder from '@atlassian/jira-placeholder';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
	FireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types/src/application.tsx';
import { getFeatureAnalyticsAttributes } from '@atlassian/jira-software-feature-analytics/src/common/utils.tsx';
import Spinner from '@atlassian/jira-software-report-charting-common/src/ui/spinner/index.tsx';
import responseToSla from '@atlassian/jira-software-sla-tracker/src/services/response-to-sla/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { LOCATION } from '../common/constants';
import { isErrorTrackingEnabledWithSentry, isPollinatorTenant } from '../feature-flags';
import CFDQuery from '../services/cfd-query';
import { cumulativeFlowDiagramPageLoad } from '../services/utils/performance-analytics';
import type { PropsNoIntl as ChartProps } from './chart/types';
import Controls from './controls';
import messages from './messages';
import type { Props, FullReportProps } from './types';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyChart = lazy(
	() => import(/* webpackChunkName: "cumulative-flow-diagram-chart-lazy" */ './chart'),
	{ ssr: false },
);

const Chart = (props: ChartProps) => (
	<JSErrorBoundary
		fallback="flag"
		packageName="jiraReportsCumulativeFlowDiagram"
		id="bundle-async-cumulative-flow-diagram-chart-lazy"
	>
		<Placeholder name="cumulative-flow-diagram-chart-lazy" fallback={<Spinner />}>
			<LazyChart {...props} />
		</Placeholder>
	</JSErrorBoundary>
);

const FullReport = ({ data, onExperienceSuccess }: FullReportProps) => {
	useEffect(() => {
		onExperienceSuccess();
	}, [onExperienceSuccess]);

	return (
		<>
			<Controls isDisabled={false} lowerOpacity={false} />
			<Chart filters={data.filters} data={data.chart} />
		</>
	);
};

const CumulativeFlowDiagramView = ({ boardId, intl }: Props) => {
	// @ts-expect-error - TS7031 - Binding element 'refetch' implicitly has an 'any' type. | TS7031 - Binding element 'error' implicitly has an 'any' type. | TS7031 - Binding element 'loading' implicitly has an 'any' type. | TS7031 - Binding element 'data' implicitly has an 'any' type.
	const onDataChange = ({ refetch, error, loading, data }) => {
		if (!loading && error) {
			return (
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="cumulativeFlowDiagramError">
					{responseToSla(error, LOCATION)}
					<FireScreenAnalytics />
					<Controls isDisabled lowerOpacity />
					<EmptyState
						renderImage={getAkEmptyStateRenderImageFn(ErrorImageUrl)}
						header={intl.formatMessage(messages.fetchError)}
						primaryAction={
							<Button
								appearance="primary"
								onClick={() => {
									refetch();
								}}
							>
								{intl.formatMessage(messages.retryOnError)}
							</Button>
						}
					/>
					<FireOperationalAnalytics eventName="cumulativeFlowDiagramReport failed" />
				</ContextualAnalyticsData>
			);
		}

		const sprintsFeature = !loading
			? // @ts-expect-error - TS7031 - Binding element 'key' implicitly has an 'any' type.
				data.features && data.features.find(({ key }) => key === 'SPRINTS')
			: undefined;
		const areSprintsEnabled = sprintsFeature && sprintsFeature.status === 'ENABLED';

		const boardFeatures = !loading && data.features && getFeatureAnalyticsAttributes(data.features);

		if (!data.chart || !data.filters) {
			return (
				<>
					<Controls isDisabled lowerOpacity={false} />
					<Spinner />
				</>
			);
		}

		if (!data.chart.length) {
			return (
				<ContextualAnalyticsData
					sourceType={SCREEN}
					sourceName="cumulativeFlowDiagramEmptyState"
					attributes={{
						areSprintsEnabled,
						boardFeatures: { ...boardFeatures },
					}}
				>
					<Controls isDisabled lowerOpacity />
					<EmptyState
						renderImage={getAkEmptyStateRenderImageFn(EmptyImageUrl)}
						header={intl.formatMessage(messages.emptyReportHeader)}
						description={intl.formatMessage(messages.emptyReportDescription)}
					/>
					<SubmitApdex
						ttrSlo={8000}
						ttiSlo={8000}
						appName="software-reports-cumulative-flow-diagram"
						extra={{
							isSentryEnabled: String(isErrorTrackingEnabledWithSentry()),
							synthetic: isPollinatorTenant(),
						}}
						isExpectedToHaveSsr={false}
						// @ts-expect-error - TS2769 - No overload matches this call.
						metric={cumulativeFlowDiagramPageLoad}
					/>
					<FireScreenAnalytics />
				</ContextualAnalyticsData>
			);
		}

		return (
			<>
				<ContextualAnalyticsData
					attributes={{
						areSprintsEnabled,
						boardFeatures: { ...boardFeatures },
					}}
				>
					<ViewExperienceTracker location={LOCATION} failureEventAttributes={{}}>
						{({ onExperienceSuccess }) => (
							<FullReport data={data} onExperienceSuccess={onExperienceSuccess} />
						)}
					</ViewExperienceTracker>
				</ContextualAnalyticsData>
				<SpaStatePageReady />
			</>
		);
	};

	return (
		<TenantContextSubscriber>
			{({ tenantContext }) => (
				<ViewExperienceTrackingProvider
					experience="viewCumulativeFlowDiagram"
					analyticsSource={LOCATION}
					edition={tenantContext.appEditions.software}
					application={JIRA_SOFTWARE}
					additionalAttributes={{
						synthetic: isPollinatorTenant(),
					}}
				>
					<ContextualAnalyticsData
						sourceType={SCREEN}
						sourceName="cumulativeFlowDiagramReport"
						containerId={`${boardId}`}
						containerType="board"
						objectId={LOCATION}
						objectType="report"
						attributes={{
							boardId,
							isAdmin: tenantContext.isAdmin,
							isSiteAdmin: tenantContext.isSiteAdmin,
						}}
					>
						{/* @ts-expect-error - TS2322 - Type '({ refetch, error, loading, data }: { refetch: any; error: any; loading: any; data: any; }) => Element' is not assignable to type '((result: QueryResult<unknown, unknown>) => Element | null) & ((arg1: { loading: boolean; refetch: QueryRenderProps<Response, Variables>; error?: any; data: Record<any, any> | { ...; }; }) => ReactElement<...>)'. */}
						<CFDQuery variables={{ boardId }}>{onDataChange}</CFDQuery>
					</ContextualAnalyticsData>
				</ViewExperienceTrackingProvider>
			)}
		</TenantContextSubscriber>
	);
};
/* eslint-disable-next-line jira/js/no-inject-intl-if-functional-component */
const CumulativeFlowDiagramViewWithIntl = injectIntl(CumulativeFlowDiagramView);
export default CumulativeFlowDiagramViewWithIntl;
